import React, { useEffect, useState, useMemo } from "react";
import { api, formatCurrency, formatDate, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Money } from "@phosphor-icons/react";
import { toast } from "sonner";
import FileAttachment from "../components/app/FileAttachment";

const STATI = [
  { v: "incassato", label: "Incassato", cls: "bg-green-100 text-green-800 border-green-300" },
  { v: "parziale", label: "Parziale", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { v: "in_attesa", label: "In Attesa", cls: "bg-gray-100 text-gray-800 border-gray-300" },
];

const EMPTY = {
  cliente_id: "", quote_id: "", importo: 0, data: new Date().toISOString().slice(0, 10),
  metodo: "Bonifico bancario", descrizione: "", stato: "incassato", allegato_id: null, note: "",
};

export default function Incassi() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStato, setFilterStato] = useState("tutti");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [i, c, q] = await Promise.all([
        api.get("/incassi"),
        api.get("/clients"),
        api.get("/quotes"),
      ]);
      setItems(i.data);
      setClients(c.data);
      setQuotes(q.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setForm({ ...EMPTY, metodo: settings?.metodi_pagamento?.[0] || "Bonifico bancario" }); setEditing("new"); }
  function openEdit(it) { setForm({ ...EMPTY, ...it, data: (it.data || "").slice(0, 10) }); setEditing(it); }
  function close() { setEditing(null); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, importo: Number(form.importo), quote_id: form.quote_id || null };
      if (editing === "new") {
        await api.post("/incassi", payload);
        toast.success("Incasso registrato");
      } else {
        await api.put(`/incassi/${editing.id}`, payload);
        toast.success("Incasso aggiornato");
      }
      close();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  }

  async function remove(it) {
    if (!window.confirm(`Eliminare incasso di ${formatCurrency(it.importo)}?`)) return;
    try {
      await api.delete(`/incassi/${it.id}`);
      toast.success("Eliminato");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  }

  const totaleIncassato = useMemo(() =>
    items.filter(i => i.stato === "incassato").reduce((s, i) => s + (i.importo || 0), 0)
  , [items]);
  const totaleAttesa = useMemo(() =>
    items.filter(i => i.stato !== "incassato").reduce((s, i) => s + (i.importo || 0), 0)
  , [items]);

  const filtered = items.filter((it) => {
    const text = [it.cliente_nome, it.descrizione, it.metodo].filter(Boolean).join(" ").toLowerCase();
    const matchSearch = text.includes(search.toLowerCase());
    const matchStato = filterStato === "tutti" || it.stato === filterStato;
    return matchSearch && matchStato;
  });

  function clientQuotes(id) { return quotes.filter(q => q.cliente_id === id); }

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Entrate</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Incassi</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.length} registrati</p>
        </div>
        <button onClick={openNew} data-testid="new-incasso-btn" className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start">
          <Plus size={14} weight="bold" /> Nuovo Incasso
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="swiss-card p-6 bg-[#008A3C] text-white border-[#008A3C]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Totale Incassato</div>
          <div className="font-display text-3xl font-black tracking-tighter mt-2">{formatCurrency(totaleIncassato)}</div>
        </div>
        <div className="swiss-card p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">In Attesa / Parziale</div>
          <div className="font-display text-3xl font-black tracking-tighter mt-2">{formatCurrency(totaleAttesa)}</div>
        </div>
      </div>

      <div className="swiss-card">
        <div className="p-4 border-b border-[#E2E8F0] flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input placeholder="Cerca cliente, descrizione..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="incasso-search" className="w-full bg-white border border-[#E2E8F0] rounded-sm pl-10 pr-3 py-2 text-sm focus:border-[#0033A0] outline-none" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {["tutti", ...STATI.map(s => s.v)].map((s) => (
              <button key={s} onClick={() => setFilterStato(s)} className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm border transition-all ${filterStato === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0A0A0A]"}`}>
                {s === "tutti" ? "Tutti" : STATI.find(x => x.v === s)?.label || s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#64748B]">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Money size={32} className="mx-auto text-[#64748B] mb-3" />
            <p className="text-sm text-[#64748B]">{items.length === 0 ? "Nessun incasso registrato." : "Nessun risultato."}</p>
          </div>
        ) : (
          <>
            {/* Tabella desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F5F7]">
                  <tr className="text-left">
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Data</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Cliente</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Metodo</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Descrizione</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Stato</th>
                    <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold text-right">Importo</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => {
                    const stato = STATI.find(s => s.v === it.stato) || STATI[2];
                    return (
                      <tr key={it.id} className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7]" data-testid={`incasso-row-${it.id}`}>
                        <td className="py-3 px-4 font-mono text-xs">{formatDate(it.data)}</td>
                        <td className="py-3 px-4 font-semibold">{it.cliente_nome}</td>
                        <td className="py-3 px-4 text-[#64748B]">{it.metodo}</td>
                        <td className="py-3 px-4 text-[#64748B] max-w-xs truncate">{it.descrizione || "—"}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${stato.cls}`}>{stato.label}</span>
                        </td>
                        <td className="py-3 px-4 text-right font-mono font-bold">{formatCurrency(it.importo)}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="inline-flex gap-1">
                            <button onClick={() => openEdit(it)} className="p-1.5 text-[#64748B] hover:text-[#0033A0]" data-testid={`edit-incasso-${it.id}`}><PencilSimple size={16} /></button>
                            <button onClick={() => remove(it)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" data-testid={`delete-incasso-${it.id}`}><Trash size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Card stack mobile */}
            <div className="md:hidden divide-y divide-[#E2E8F0]">
              {filtered.map((it) => {
                const stato = STATI.find(s => s.v === it.stato) || STATI[2];
                return (
                  <div key={it.id} className="px-4 py-4" data-testid={`incasso-card-${it.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${stato.cls}`}>{stato.label}</span>
                          <span className="font-mono text-[10px] text-[#64748B]">{formatDate(it.data)}</span>
                        </div>
                        <div className="font-semibold text-sm truncate">{it.cliente_nome}</div>
                        <div className="text-[11px] text-[#64748B] mt-0.5 truncate">{it.metodo}{it.descrizione ? ` · ${it.descrizione}` : ""}</div>
                      </div>
                      <div className="font-mono font-bold text-sm flex-shrink-0">{formatCurrency(it.importo)}</div>
                    </div>
                    <div className="flex justify-end gap-1 mt-2">
                      <button onClick={() => openEdit(it)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0] px-2 py-1" data-testid={`edit-incasso-mobile-${it.id}`}>Modifica</button>
                      <button onClick={() => remove(it)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] px-2 py-1 -mr-2" data-testid={`delete-incasso-mobile-${it.id}`}>Elimina</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-sm max-w-2xl w-full max-h-[90vh] overflow-auto thin-scroll" data-testid="incasso-modal">
            <div className="p-6 border-b border-[#E2E8F0] sticky top-0 bg-white">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">{editing === "new" ? "Nuovo" : "Modifica"}</div>
              <h2 className="font-display text-2xl font-bold">Incasso</h2>
            </div>
            <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Cliente *" full>
                <select required value={form.cliente_id} onChange={(e) => setForm({ ...form, cliente_id: e.target.value, quote_id: "" })} className="inp" data-testid="incasso-cliente">
                  <option value="">— Seleziona —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
                </select>
              </Field>
              <Field label="Preventivo (opzionale)" full>
                <select value={form.quote_id || ""} onChange={(e) => setForm({ ...form, quote_id: e.target.value })} className="inp" disabled={!form.cliente_id}>
                  <option value="">— Nessuno —</option>
                  {clientQuotes(form.cliente_id).map(q => <option key={q.id} value={q.id}>{q.numero} · {formatCurrency(q.totale)}</option>)}
                </select>
              </Field>
              <Field label="Importo *"><input type="number" min={0} step="0.01" required value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} className="inp font-mono" data-testid="incasso-importo" /></Field>
              <Field label="Data *"><input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="inp" /></Field>
              <Field label="Metodo">
                <select value={form.metodo} onChange={(e) => setForm({ ...form, metodo: e.target.value })} className="inp">
                  {(settings?.metodi_pagamento || []).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Stato">
                <select value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })} className="inp">
                  {STATI.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Descrizione" full><input value={form.descrizione || ""} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="inp" /></Field>
              <Field label="Note" full><textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="inp" /></Field>
              <div className="col-span-2">
                <FileAttachment value={form.allegato_id} onChange={(id) => setForm({ ...form, allegato_id: id })} label="Allegato (ricevuta)" testid="incasso-allegato" />
              </div>
            </div>
            <div className="p-6 border-t border-[#E2E8F0] flex justify-end gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={close} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Annulla</button>
              <button type="submit" disabled={saving} className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="incasso-save-btn">{saving ? "…" : "Salva"}</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp { width: 100%; background: #fff; border: 1px solid #E2E8F0; border-radius: 4px; padding: 8px 12px; font-size: 14px; outline: none; } .inp:focus { border-color: #0033A0; box-shadow: 0 0 0 1px #0033A0; }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
